<template>
  <v-row class="d-flex h-100">
    <v-alert
      v-if="storesWithNoCoordinates.length"
      type="info"
      variant="tonal"
      class="ma-2 info-message w-100"
    >
      {{ tkey('infoMessage', { stores: storesWithNoCoordinates.map(s => s.storeKeyDisplay) }) }}
    </v-alert>
    <div
      class="position-relative w-100 h-100 leaflet-container leaflet-touch leaflet-fade-anim leaflet-grab leaflet-touch-drag leaflet-touch-zoom"
      tabindex="0"
    >
      <div id="map" class="w-100" style="height: 100%" />
      <Sidepanel v-if="showSidepanel" :title="tkey('legend')" :toggle-btn-text="tkey('legend')">
        <v-card variant="outlined" class="legend">
          <v-list class="py-0" color="primary">
            <v-list-item
              v-for="cluster in schemeClusters"
              :key="cluster.clusterId"
              :value="cluster.clusterId"
            >
              <v-list-item-title class="cluster">
                <v-row class="cluster-name align-center">
                  <v-col :cols="2">
                    <v-avatar :color="markerColourOptions[cluster.colour]" size="30" />
                  </v-col>
                  <v-col :cols="7">
                    {{ cluster.clusterName }}<br />
                    <b>{{ tkey('stores') }}: </b> {{ cluster.storeKeys.length }}
                  </v-col>
                  <v-col :cols="3">
                    <v-checkbox v-model="legendSelectedClusters" :value="cluster.clusterId" />
                  </v-col>
                </v-row>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </Sidepanel>
    </div>
  </v-row>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import owColours from '@/plugins/ow-colors';
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import '@/utils/titleLayer.grayscale';
import L from 'leaflet';
import 'leaflet.awesome-markers';
import 'leaflet.markercluster';
import { find, get, isEmpty, max, min, pick } from 'lodash';
import { mapState } from 'pinia';

const markerColourOptions = owColours.clusteringColours;
export default {
  setup() {
    const { tkey } = useInternationalization('clusteringPage.tabs.storeAllocation.map');
    return { tkey };
  },

  data() {
    return {
      markerColourOptions,
      markerColours: Object.keys(markerColourOptions), // sensible ordered colours
      selectedColours: [],
      clusterColours: {}, // Map of clusterId -> colour
      mapLayers: {},
      showSidepanel: true,
      legendSelectedClusters: [],
      map: null,
      markers: [],
      storesWithNoCoordinates: [],
    };
  },

  computed: {
    ...mapState(useContextStore, ['clientConfig']),
    ...mapState(useClusteringStore, ['selectedScheme']),
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),
    ...mapState(useScenariosStore, ['stores']),

    storesGroupedByScheme() {
      const stores = [];
      this.schemeClusters.forEach(c => {
        const { clusterName, clusterId } = c;
        c.storeKeys.forEach(storeKey => {
          const store = pick(find(this.stores, { storeKey }), [
            'storeKeyDisplay',
            'formatDescription',
            'storeDescription',
            'latitude',
            'longitude',
          ]);

          stores.push({ clusterName, clusterId, ...store });
        });
      });
      return stores;
    },

    schemeClusters() {
      return this.selectedScheme.clusters.map(cluster => {
        return {
          ...cluster,
          colour: this.clusterColours[cluster.clusterId] || '',
        };
      });
    },
  },

  watch: {
    legendSelectedClusters() {
      // remove markers
      Object.keys(this.mapLayers).forEach(sc => {
        this.mapLayers[sc].removeFrom(this.map);
      });

      // apply markers from selected layers
      this.legendSelectedClusters.forEach(sc => {
        this.mapLayers[sc].addTo(this.map);
      });
      this.applyMapBoundaries();
    },
  },

  beforeUnmount() {
    // Clean up the map resources when we navigate away.
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  mounted() {
    this.assignClusterColours();

    // Create map
    const { attributionControl, defaultCenter } = this.clientConfig.clusteringMap;
    let { defaultZoom } = this.clientConfig.clusteringMap;
    if (this.storesWithNoCoordinates.length === this.storesGroupedByScheme.length) {
      defaultZoom = 2;
    }

    this.map = L.map('map', {
      center: defaultCenter,
      zoom: defaultZoom,
      minZoom: 2, // min zoom of 2 defaults to the world view
      layers: [this.createGrayScaleLayer()],
      attributionControl,
    });

    // Create map marker cluster layers
    const layerGroups = {};
    this.schemeClusters.forEach(cluster => {
      layerGroups[cluster.clusterId] = this.registerGroupLayer(cluster);
    });

    // select all markers
    this.legendSelectedClusters = Object.keys(layerGroups);
    this.legendSelectedClusters.forEach(k => {
      const layer = get(layerGroups[k], 'markers');
      if (layer) {
        this.mapLayers[k] = layer;
      }
    });
  },

  methods: {
    /**
     * Assigns colours to the clusters in the selected scheme. These are used on the map and in the legend.
     */
    assignClusterColours() {
      // Reset colour tracking
      this.selectedColours = [];
      this.clusterColours = {};

      // Assign a colour to each cluster once
      this.selectedScheme.clusters.forEach(cluster => {
        if (!this.clusterColours[cluster.clusterId]) {
          this.clusterColours[cluster.clusterId] = this.pickColour();
        }
      });
    },

    createGrayScaleLayer() {
      const { uiId, defaultMapUI } = this.clientConfig.clusteringMap;

      return L.tileLayer.grayscale(defaultMapUI, { id: uiId });
    },

    registerGroupLayer(cluster) {
      const { clusterId, colour } = cluster;
      const markerClusterGroup = L.markerClusterGroup({
        iconCreateFunction: c =>
          L.divIcon({
            className: `awesome-marker-icon-${colour} awesome-marker`,
            html: `<span class="awesome-marker__count">${c.getChildCount()}</span>`,
            iconSize: [35, 45],
          }),
      });
      // add auto generated marker icon
      const stores = this.storesGroupedByScheme.filter(
        store => store.clusterId === cluster.clusterId
      );
      stores.forEach(store => {
        const latitude = get(store, 'latitude');
        const longitude = get(store, 'longitude');
        if (!latitude || !longitude) {
          this.storesWithNoCoordinates.push(store);
        } else {
          this.createMarker(store, colour).addTo(markerClusterGroup);
          this.markers.push({ latitude, longitude, clusterId });
        }
      });
      this.map.addLayer(markerClusterGroup);
      return { markers: markerClusterGroup };
    },

    createMarker(store, colour) {
      const { latitude, longitude } = store;
      const markerIcon = L.AwesomeMarkers.icon({ markerColor: colour });
      const markerTooltipText = this.getTooltipMessage(store);
      return L.marker([latitude, longitude], { icon: markerIcon }).bindPopup(markerTooltipText);
    },

    getTooltipMessage(store) {
      return `<b>${store.storeKeyDisplay}</b><br /><b>${store.storeDescription}</b><br />${store.formatDescription}`;
    },

    applyMapBoundaries() {
      const latitudes = [];
      const longitudes = [];

      this.legendSelectedClusters.forEach(sc => {
        const markers = this.markers.filter(m => m.clusterId === sc);
        markers.forEach(m => {
          latitudes.push(get(m, 'latitude'));
          longitudes.push(get(m, 'longitude'));
        });
      });

      // calculate map boundaries
      const southWest = L.latLng(min(latitudes), min(longitudes)) || 0;
      const northEast = L.latLng(max(latitudes), max(longitudes)) || 0;

      // create bounds
      const bounds = L.latLngBounds(southWest, northEast);
      if (!isEmpty(bounds)) {
        // if no layer is selected there is no bounds
        this.map.fitBounds(bounds);
      }
    },

    pickColour() {
      let colour;
      for (let i = 0; i < this.markerColours.length; i += 1) {
        const c = this.markerColours[i];
        if (!this.selectedColours.includes(c)) {
          this.selectedColours.push(c);
          colour = c;
          break;
        }
      }
      return colour;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.leaflet-container) {
  z-index: 200;

  .sidepanel {
    z-index: 201;
  }
}

:deep(.leaflet-popup-content-wrapper),
:deep(.leaflet-popup-tip) {
  border-radius: 0 !important;

  b {
    font-size: 1.2em;
  }
}

:deep(.awesome-marker) {
  padding-top: 10px;
  text-align: center;

  .awesome-marker__count {
    font-weight: bold;
  }
}

:deep(.sidepanel__header) {
  display: none;
}

.legend {
  border: none !important;
  .v-item-group {
    min-width: 190px;
  }
  .v-list {
    padding: 0;
    border: none !important;
  }
  .v-list-item {
    padding: 0;
  }
  .-list-item {
    padding: 0;
  }

  .v-list-item__content {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    background-color: $legend-bg;
    border-bottom: $legend-border 1px solid;
  }
}
</style>
